import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import '@fontsource-variable/inter';
import '@fontsource/noto-serif/greek-400.css';
import '@fontsource/noto-serif/greek-600.css';
import '@fontsource/noto-serif/latin-400.css';
import './styles/index.css';
import { App } from './App';
import { I18nProvider } from './i18n';
import { AuthWatcher } from './lib/auth';
import { TooltipProvider } from './components/ui/misc';
import { applyTheme, usePrefs } from './hooks/usePrefs';
import { UpdatePrompt } from './components/UpdatePrompt';
import { drafts } from './lib/drafts';

const qc = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: 1 },
    mutations: { retry: 0 },
  },
});

function ThemeSync() {
  const [prefs] = usePrefs();
  React.useEffect(() => applyTheme(prefs.theme), [prefs.theme]);
  return null;
}

// Allow one automatic chunk-reload again once the app has run fine for a while (prevents reload loops).
setTimeout(() => {
  try {
    sessionStorage.removeItem('gn.chunkReload');
  } catch {
    /* ignore */
  }
}, 15_000);
// Ask the browser to keep drafts (best effort; iOS may still evict — the server is canonical).
void drafts.persist();

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <I18nProvider>
      <QueryClientProvider client={qc}>
        <TooltipProvider>
          <AuthWatcher />
          <ThemeSync />
          <App />
          <UpdatePrompt />
          <Toaster position="top-center" richColors closeButton toastOptions={{ style: { fontFamily: 'var(--font-ui)' } }} />
        </TooltipProvider>
      </QueryClientProvider>
    </I18nProvider>
  </React.StrictMode>,
);
